import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Briefcase, Globe, Star, Shield } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const FounderPage = () => {
  const { t } = useTranslation();

  const values = [
    { icon: Star, title: t("founder.value_2_title"), description: t("founder.value_2_desc") },
    { icon: Shield, title: t("founder.value_1_title"), description: t("founder.value_1_desc") },
    { icon: Briefcase, title: t("founder.value_3_title"), description: t("founder.value_3_desc") },
    { icon: Globe, title: t("founder.value_4_title"), description: t("founder.value_4_desc") },
  ];

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="founder-page">
      <SEO title="Fondateur — Adrien Garcia" path="/fondateur" description="Découvrez Adrien Garcia, fondateur d'ALTURA. Sa vision : offrir un service de transport privé d'exception à Paris, alliant élégance et confidentialité." />
      <section className="py-24 sm:py-32 bg-[#EDEBE4] relative">
        <div className="texture-overlay" />
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="order-2 lg:order-1">
              <div className="relative">
                <img src="https://customer-assets.emergentagent.com/job_parisgo/artifacts/354qk501_3AB0A195-06A8-43CE-9CB9-44100D45C5E9.png" alt="Adrien Garcia - Fondateur d'ALTURA" className="w-full max-w-md mx-auto lg:mx-0" />
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#EDEBE4] to-transparent" />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="order-1 lg:order-2">
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("founder.hero_subtitle")}</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-[#1A1A1A] leading-none mb-8">
                Adrien <span className="italic">Garcia</span>
              </h1>
              <div className="w-16 h-px bg-[#1A1A1A] mb-8" />
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-6">{t("founder.vision_p1")}</p>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed">{t("founder.vision_p2")}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#E5E3DC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">{t("founder.values_subtitle")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-normal text-[#1A1A1A]">
              {t("founder.values_title_1")} <span className="italic">{t("founder.values_title_2")}</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-[#1A1A1A]/10 p-8 text-center">
                <value.icon size={36} weight="thin" className="text-[#1A1A1A] mx-auto mb-6" />
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-4">{value.title}</h3>
                <p className="text-[#1A1A1A]/70 font-light text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#E5E3DC] relative">
        <div className="texture-overlay" />
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl sm:text-4xl font-normal text-[#1A1A1A] mb-8">{t("founder.cta_title")}</h2>
            <p className="text-[#1A1A1A]/80 font-light mb-12 max-w-2xl mx-auto">{t("founder.cta_desc")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:contact@alturaconciergerie.com" className="btn-dark inline-flex items-center gap-3 justify-center" data-testid="founder-email-cta">
                {t("founder.cta_email")}
                <ArrowRight size={16} weight="bold" />
              </a>
              <a href="tel:+33681969342" className="btn-primary inline-flex items-center gap-3 justify-center" data-testid="founder-call-cta">
                {t("founder.cta_call")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default FounderPage;
